import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Radio, Info } from "lucide-react";

// Admin kill switch for notary-routing notifications. Off => no notifications fan out and behavior
// matches pre-Phase-4 (pool stays fully visible + claimable). Every toggle is audited server-side.
export const NotaryRoutingKillSwitch = () => {
  const [enabled, setEnabled] = useState(true);
  const [note, setNote] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/notary-routing-settings")
      .then(({ data }) => { setEnabled(data.enabled !== false); setNote(data.note || ""); setLoaded(true); })
      .catch(() => setLoaded(true));
  }, []);

  const toggle = async () => {
    const next = !enabled;
    setEnabled(next);
    setSaving(true);
    try {
      await api.put("/admin/notary-routing-settings", { enabled: next });
      toast.success(next ? "Routing notifications ON" : "Routing notifications OFF — pool stays claimable");
    } catch (e) {
      setEnabled(!next);
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not update");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mb-6 rounded-xl border border-slate-200 bg-white p-4" data-testid="notary-routing-killswitch-card">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-start gap-3">
          <Radio className={`mt-0.5 h-5 w-5 ${enabled ? "text-emerald-500" : "text-slate-400"}`} />
          <div>
            <h3 className="font-display text-base font-bold text-slate-900">Notary routing notifications</h3>
            <p className="mt-0.5 text-sm text-slate-500">
              {enabled ? "On — providers are notified as orders route through their county tiers."
                       : "Off — no routing notifications are sent. The notary pool stays fully visible and claimable."}
            </p>
          </div>
        </div>
        <button
          type="button" role="switch" aria-checked={enabled}
          data-testid="notary-routing-killswitch-toggle"
          disabled={!loaded || saving} onClick={toggle}
          className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${enabled ? "bg-emerald-600" : "bg-slate-300"} ${(!loaded || saving) ? "opacity-60" : ""}`}
        >
          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${enabled ? "translate-x-6" : "translate-x-1"}`} />
        </button>
      </div>
      {note && (
        <p className="mt-3 flex items-start gap-1.5 border-t border-slate-100 pt-2.5 text-xs text-slate-400" data-testid="notary-routing-timing-note">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {note}
        </p>
      )}
    </div>
  );
};
